import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { SidebarNavMenuItem } from '../../../decorators/sidebar-nav-menu-item';
import { Router } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { NotificationsService } from '../../../services/home-services/notifications.service';
import { ErrorHandlerService } from '../../../decoration-base/error-handler.service';
import { Log, IShortLogger } from '../../../services/logger.service';
import { DEFAULT_PAGINATOR_PAGE_SIZE, PAGINATOR_PAGE_SIZE_OPTIONS } from '../../../shared/pagination-options';

interface Notification {
  id: string;
  toUser: string;
  fromUser?: string;
  fromUserName?: string;
  fromUserTeam?: string;
  timeStamp: string;
  contents: string;
  notifiableIfRead: boolean;
  notificationType: string;
  actionType: string;
  actionData: string;
  isRead: boolean;
  status: string;
}

@SidebarNavMenuItem({
  route: '/notifications',
  faIcon: 'notifications_none',
  name: 'Notifications',
  order: 10000,
  businessProcessesName: 'Notifications'
})
@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  styleUrl: './notifications.component.css'
})
export class NotificationsComponent implements OnInit, AfterViewInit {
  private readonly logger: IShortLogger<NotificationsComponent> = Log.create(NotificationsComponent);

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  notifications: Notification[] = [];
  dataSource = new MatTableDataSource<Notification>([]);
  readonly displayedColumns = ['readStatus', 'type', 'from', 'message', 'time', 'actions'];
  readonly pageSizeOptions = PAGINATOR_PAGE_SIZE_OPTIONS;
  readonly pageSize = DEFAULT_PAGINATOR_PAGE_SIZE;

  selectedFilter: 'all' | 'unread' = 'all';
  searchTerm = '';
  isLoading = false;

  public userId: string | null = null;

  constructor(
    private router: Router,
    private notificationService: NotificationsService,
    private errorHandler: ErrorHandlerService
  ) {}

  ngOnInit(): void {
    this.userId = localStorage.getItem('userId');

    this.dataSource.filterPredicate = (row: Notification, filter: string) =>
      (row.contents?.toLowerCase().includes(filter) ?? false)
      || (row.notificationType?.toLowerCase().includes(filter) ?? false)
      || (row.fromUserName?.toLowerCase().includes(filter) ?? false)
      || (row.fromUserTeam?.toLowerCase().includes(filter) ?? false);

    this.dataSource.sortingDataAccessor = (row: Notification, sortHeaderId: string) => {
      switch (sortHeaderId) {
        case 'type':       return row.notificationType?.toLowerCase() ?? '';
        case 'from':       return row.fromUserName?.toLowerCase() ?? '';
        case 'message':    return row.contents?.toLowerCase() ?? '';
        case 'time':       return row.timeStamp ? new Date(row.timeStamp).getTime() : 0;
        case 'readStatus': return row.isRead ? 1 : 0;
        default:           return '';
      }
    };

    if (this.userId) {
      this.loadNotifications(this.userId);
    }
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  loadNotifications(userId: string): void {
    this.isLoading = true;
    this.notificationService.getNotifactionsForUsers(userId).subscribe({
      next: (response: Notification[]) => {
        this.notifications = response || [];
        this.isLoading = false;
        this.applyFilter();
      },
      error: (error) => {
        this.isLoading = false;
        this.errorHandler.handleError(error, 'Failed to load notifications');
      }
    });
  }

  applyFilter(): void {
    const filtered = this.selectedFilter === 'unread'
      ? this.notifications.filter(n => !n.isRead)
      : [...this.notifications];
    this.dataSource.data = filtered;
    this.dataSource.filter = this.searchTerm.trim().toLowerCase();
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  onSearchChange(): void {
    this.dataSource.filter = this.searchTerm.trim().toLowerCase();
  }

  setFilter(filter: 'all' | 'unread'): void {
    this.selectedFilter = filter;
    this.applyFilter();
  }

  getUnreadCount(): number {
    return this.notifications.filter(n => !n.isRead).length;
  }

  formatTimestamp(timestamp: string): string {
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return '';
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const inputDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const timeStr = date.toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: false });
      if (inputDate.getTime() === today.getTime())     return timeStr;
      if (inputDate.getTime() === yesterday.getTime()) return `Yesterday ${timeStr}`;
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) + ' ' + timeStr;
    } catch {
      return '';
    }
  }

  getFromDisplay(n: Notification): string {
    return n.fromUserName || n.fromUserTeam || '';
  }

  markAsRead(notification: Notification, event: Event): void {
    event.stopPropagation();
    if (notification.isRead) return;
    notification.isRead = true;
    this.notificationService.markNotificationAsRead(notification.id).subscribe({
      next: () => this.applyFilter(),
      error: (err) => this.logger.e('Error marking as read:', err)
    });
  }

  performAction(notification: Notification, event: Event): void {
    event.stopPropagation();
    // Mark as read in the background whenever the user actions a notification
    if (!notification.isRead) {
      notification.isRead = true;
      this.notificationService.markNotificationAsRead(notification.id).subscribe({
        next: () => this.applyFilter(),
        error: (err) => this.logger.e('Error marking as read from action:', err)
      });
    }
    try {
      const actionData = JSON.parse(notification.actionData);
      const type = (notification.notificationType ?? '').toLowerCase();

      if (type === 'lrc filling') {
        this.router.navigate(['/trainer-requisition-view', actionData.ReqId]);
      } else if (type === 'cloudsettings' || type === 'cloud settings') {
        if (actionData.ClassId) {
          this.router.navigate(['/class', actionData.ClassId], { queryParams: { tab: 'settings' } });
        } else if (actionData.ReqId) {
          this.router.navigate(['/requisition-preview', actionData.ReqId, 'hide']);
        }
      } else if (type === 'quotaandcleanuppolicysettings') {
        if (actionData.ClassId) {
          this.router.navigate(['/policy-template-linking'], { queryParams: { classId: actionData.ClassId } });
        } else if (actionData.ReqId) {
          this.router.navigate(['/requisition-preview', actionData.ReqId, 'hide']);
        }
      } else {
        this.logger.i('Unhandled notification action type:', notification.notificationType);
      }
    } catch (e) {
      this.logger.e('Error parsing notification action data:', e);
    }
  }
}
